import { computed, ref } from 'vue'

// Keyboard shortcuts: one registry for every rebindable action, the bindings the user picked in
// Settings → «Горячие клавиши» (kept on this device), and the matching used by every handler.
//
// A binding is a string of `+`-joined parts, modifiers first: `Mod+F`, `Mod+Enter`, `Shift+Alt+K`,
// `Escape`, `ArrowUp`. `Mod` is ⌘ on macOS and Ctrl elsewhere, so a default reads naturally on both.
// The main key is the *physical* key for letters and digits (`KeyboardEvent.code`), so a shortcut
// keeps working under any keyboard layout — ⌘F used to do nothing with the Russian layout on,
// where that key types «а».

export type HotkeyId =
  | 'nav.quickSwitch'
  | 'nav.browseChannels'
  | 'nav.newChannel'
  | 'nav.settings'
  | 'nav.back'
  | 'nav.forward'
  | 'nav.home'
  | 'nav.toggleSidebar'
  | 'search.open'
  | 'read.markChannel'
  | 'read.markAll'
  | 'common.cancel'
  | 'composer.send'
  | 'composer.editLast'
  | 'format.bold'
  | 'format.italic'
  | 'format.strike'
  | 'format.code'
  | 'format.link'
  | 'voice.toggleMic'
  | 'voice.toggleDeafen'
  | 'voice.leave'
  | 'voice.pushToTalk'
  | 'zoom.in'
  | 'zoom.out'
  | 'zoom.reset'

/**
 * Where an action listens. `global` — anywhere in the app; `composer` — in the message field, where
 * it wins over a global action on the same keys (⌘K: insert a link while typing, quick switch
 * elsewhere). Only actions of the same scope conflict.
 */
export type HotkeyScope = 'global' | 'composer'

export interface HotkeyAction {
  id: HotkeyId
  group: string
  title: string
  description: string
  defaultBinding: string
  scope: HotkeyScope
  /** Runs on the same keys only when nothing else took them — no conflict with that action
   * (Escape marks the channel read once there's nothing left to close or cancel). */
  fallbackOf?: HotkeyId
}

export const HOTKEY_ACTIONS: HotkeyAction[] = [
  // ── Навигация ──
  { id: 'nav.quickSwitch', group: 'Навигация', title: 'Переход', description: 'К группе, каналу или другу', defaultBinding: 'Mod+K', scope: 'global' },
  { id: 'nav.browseChannels', group: 'Навигация', title: 'Каналы группы', description: 'Список каналов с поиском', defaultBinding: 'Mod+Shift+O', scope: 'global' },
  { id: 'nav.newChannel', group: 'Навигация', title: 'Новый канал', description: 'В текущей группе', defaultBinding: 'Mod+Shift+N', scope: 'global' },
  { id: 'nav.settings', group: 'Навигация', title: 'Настройки', description: 'Открыть или закрыть', defaultBinding: 'Mod+,', scope: 'global' },
  { id: 'nav.back', group: 'Навигация', title: 'Назад', description: 'Предыдущая страница', defaultBinding: 'Mod+[', scope: 'global' },
  { id: 'nav.forward', group: 'Навигация', title: 'Вперёд', description: 'Следующая страница', defaultBinding: 'Mod+]', scope: 'global' },
  { id: 'search.open', group: 'Навигация', title: 'Поиск в канале', description: 'По сообщениям канала', defaultBinding: 'Mod+F', scope: 'global' },
  { id: 'nav.home', group: 'Навигация', title: 'Главная', description: 'Домашняя страница', defaultBinding: 'Mod+Shift+A', scope: 'global' },
  { id: 'nav.toggleSidebar', group: 'Навигация', title: 'Боковая панель', description: 'Свернуть или развернуть', defaultBinding: 'Mod+S', scope: 'global' },
  { id: 'read.markChannel', group: 'Навигация', title: 'Прочитать канал', description: 'Если нечего закрыть', defaultBinding: 'Escape', scope: 'global', fallbackOf: 'common.cancel' },
  { id: 'read.markAll', group: 'Навигация', title: 'Прочитать всё', description: 'Все каналы всех групп', defaultBinding: 'Shift+Escape', scope: 'global' },
  { id: 'common.cancel', group: 'Навигация', title: 'Закрыть', description: 'Поиск, ответ, правку, выделение', defaultBinding: 'Escape', scope: 'global' },

  // ── Сообщения ──
  { id: 'composer.send', group: 'Сообщения', title: 'Отправить', description: 'С модификатором Enter переносит строку', defaultBinding: 'Enter', scope: 'composer' },
  { id: 'composer.editLast', group: 'Сообщения', title: 'Изменить последнее', description: 'В пустом поле ввода', defaultBinding: 'ArrowUp', scope: 'composer' },

  // ── Форматирование ──
  { id: 'format.bold', group: 'Форматирование', title: 'Жирный', description: '**текст**', defaultBinding: 'Mod+B', scope: 'composer' },
  { id: 'format.italic', group: 'Форматирование', title: 'Курсив', description: '*текст*', defaultBinding: 'Mod+I', scope: 'composer' },
  { id: 'format.strike', group: 'Форматирование', title: 'Зачёркнутый', description: '~~текст~~', defaultBinding: 'Mod+Shift+X', scope: 'composer' },
  { id: 'format.code', group: 'Форматирование', title: 'Код', description: '`текст`', defaultBinding: 'Mod+E', scope: 'composer' },
  { id: 'format.link', group: 'Форматирование', title: 'Ссылка', description: '[текст](адрес)', defaultBinding: 'Mod+K', scope: 'composer' },

  // ── Голос ──
  { id: 'voice.toggleMic', group: 'Голос', title: 'Микрофон', description: 'Вкл/выкл, и вне звонка', defaultBinding: 'Mod+Shift+M', scope: 'global' },
  { id: 'voice.toggleDeafen', group: 'Голос', title: 'Звук', description: 'Вкл/выкл звук собеседников', defaultBinding: 'Mod+Shift+D', scope: 'global' },
  { id: 'voice.leave', group: 'Голос', title: 'Выйти из звонка', description: 'Отключиться от канала', defaultBinding: 'Ctrl+Shift+Space', scope: 'global' },
  { id: 'voice.pushToTalk', group: 'Голос', title: 'Рация', description: 'Удерживать, чтобы говорить', defaultBinding: 'Ctrl+Space', scope: 'global' },

  // ── Масштаб ──
  { id: 'zoom.in', group: 'Масштаб', title: 'Крупнее', description: 'Увеличить масштаб', defaultBinding: 'Mod+=', scope: 'global' },
  { id: 'zoom.out', group: 'Масштаб', title: 'Мельче', description: 'Уменьшить масштаб', defaultBinding: 'Mod+-', scope: 'global' },
  { id: 'zoom.reset', group: 'Масштаб', title: 'Масштаб 100%', description: 'Сбросить масштаб', defaultBinding: 'Mod+0', scope: 'global' },
]

/** Shortcuts that come with the platform or the mouse — shown for reference, not rebindable. */
export const FIXED_HOTKEYS: { group: string, title: string, keys: string[] }[] = [
  { group: 'Сообщения', title: 'Перенос строки', keys: ['Shift+Enter'] },
  { group: 'Навигация', title: 'Закрыть окно или меню', keys: ['Escape'] },
  { group: 'Сообщения', title: 'Подсказки @ и /', keys: ['ArrowUp', 'ArrowDown', 'Enter', 'Tab'] },
  { group: 'Выделение', title: 'Выделить сообщение', keys: ['Mod+Клик'] },
  { group: 'Выделение', title: 'Выделить диапазон', keys: ['Shift+Клик'] },
]

const MODIFIERS = ['Mod', 'Ctrl', 'Alt', 'Shift'] as const
const STORAGE_KEY = 'hotkeys'

export const isMac = typeof navigator !== 'undefined'
  && /mac|iphone|ipad/i.test((navigator as any).userAgentData?.platform ?? navigator.platform ?? '')

// Punctuation by physical key too: under the Russian layout the «,» key types «б», «[» types «х».
const PUNCTUATION_CODES: Record<string, string> = {
  Equal: '=', Minus: '-', Comma: ',', Period: '.', Slash: '/', Semicolon: ';', Quote: "'",
  BracketLeft: '[', BracketRight: ']', Backslash: '\\', Backquote: '`',
}

/** The main key of an event, layout-independent for letters, digits and punctuation. */
export function eventKey(event: KeyboardEvent): string | null {
  if (['Meta', 'Control', 'Alt', 'Shift', 'CapsLock', 'Fn', 'OS', 'Dead', 'Process', 'Unidentified'].includes(event.key)) return null
  const code = event.code
  if (/^Key[A-Z]$/.test(code)) return code.slice(3)
  if (/^Digit[0-9]$/.test(code)) return code.slice(5)
  if (PUNCTUATION_CODES[code]) return PUNCTUATION_CODES[code]
  if (event.key === ' ') return 'Space'
  if (event.key.length === 1) return event.key.toUpperCase()
  return event.key
}

/** The binding an event represents, e.g. `Mod+Shift+K` — null for a lone modifier. */
export function bindingFromEvent(event: KeyboardEvent): string | null {
  const key = eventKey(event)
  if (!key) return null
  const parts: string[] = []
  if (isMac ? event.metaKey : event.ctrlKey) parts.push('Mod')
  if (isMac && event.ctrlKey) parts.push('Ctrl')
  if (event.altKey) parts.push('Alt')
  if (event.shiftKey) parts.push('Shift')
  parts.push(key)
  return parts.join('+')
}

function parse(binding: string) {
  const parts = binding.split('+')
  const key = parts.pop() ?? ''
  return {
    key,
    mod: parts.includes('Mod'),
    ctrl: parts.includes('Ctrl'),
    alt: parts.includes('Alt'),
    shift: parts.includes('Shift'),
  }
}

export function eventMatchesBinding(event: KeyboardEvent, binding: string): boolean {
  if (!binding) return false
  const b = parse(binding)
  // Off macOS there's one Control key: a `Ctrl+…` binding means the same as `Mod+…`.
  if (!isMac && b.ctrl) {
    b.mod = true
    b.ctrl = false
  }
  const key = eventKey(event)
  if (!key || key.toLowerCase() !== b.key.toLowerCase()) return false
  const mod = isMac ? event.metaKey : event.ctrlKey
  const ctrl = isMac ? event.ctrlKey : false
  // Off macOS, Ctrl *is* Mod; the Windows key never takes part.
  return mod === b.mod && ctrl === b.ctrl && event.altKey === b.alt && event.shiftKey === b.shift
    && (isMac || !event.metaKey)
}

const KEY_LABELS: Record<string, string> = {
  Escape: 'Esc',
  Enter: 'Enter',
  ArrowUp: '↑',
  ArrowDown: '↓',
  ArrowLeft: '←',
  ArrowRight: '→',
  Space: 'Пробел',
  Backspace: '⌫',
  Delete: 'Del',
  Tab: 'Tab',
}

/** Display chips for a binding: `['⌘', 'F']` on a Mac, `['Ctrl', 'F']` elsewhere. */
export function formatBinding(binding: string): string[] {
  if (!binding) return []
  const parts = binding.split('+')
  const key = parts.pop() ?? ''
  const mods = MODIFIERS.filter((m) => parts.includes(m)).map((m) => {
    if (m === 'Mod') return isMac ? '⌘' : 'Ctrl'
    if (m === 'Ctrl') return isMac ? '⌃' : 'Ctrl'
    if (m === 'Alt') return isMac ? '⌥' : 'Alt'
    return isMac ? '⇧' : 'Shift'
  })
  return [...mods, KEY_LABELS[key] ?? key]
}

/** Why a binding can't be used for an action, or null if it can. */
export function validateBinding(action: HotkeyAction, binding: string): string | null {
  const b = parse(binding)
  const hasModifier = b.mod || b.ctrl || b.alt
  if (action.scope === 'global' && !hasModifier && !/^(F\d{1,2}|Escape)$/.test(b.key)) {
    return 'Для глобального действия нужен модификатор (⌘/Ctrl или Alt) — иначе оно перехватит обычный ввод текста.'
  }
  if (action.scope === 'composer' && /^[A-Z0-9]$/.test(b.key) && !hasModifier) {
    return 'Одна буква или цифра будет мешать набору текста — добавьте модификатор.'
  }
  return null
}

function readOverrides(): Partial<Record<HotkeyId, string>> {
  try {
    const raw = JSON.parse(localStorage.getItem(STORAGE_KEY) || '{}')
    return raw && typeof raw === 'object' ? raw : {}
  } catch {
    return {}
  }
}

const overrides = ref<Partial<Record<HotkeyId, string>>>(typeof window !== 'undefined' ? readOverrides() : {})

function persist() {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(overrides.value))
  } catch {
    // Private mode / storage full: the change lasts for this session only.
  }
}

const actionById = new Map(HOTKEY_ACTIONS.map((a) => [a.id, a]))

export function useHotkeys() {
  const bindings = computed<Record<HotkeyId, string>>(() =>
    Object.fromEntries(HOTKEY_ACTIONS.map((a) => [a.id, overrides.value[a.id] ?? a.defaultBinding])) as Record<HotkeyId, string>)

  function bindingOf(id: HotkeyId): string {
    return bindings.value[id]
  }

  function matches(id: HotkeyId, event: KeyboardEvent): boolean {
    return eventMatchesBinding(event, bindings.value[id])
  }

  /** The other action already using `binding`, if any. */
  function conflictOf(id: HotkeyId, binding: string): HotkeyAction | null {
    const self = actionById.get(id)
    for (const action of HOTKEY_ACTIONS) {
      if (action.id === id || action.scope !== self?.scope) continue
      if (action.fallbackOf === id || self?.fallbackOf === action.id) continue
      if (bindings.value[action.id].toLowerCase() === binding.toLowerCase()) return action
    }
    return null
  }

  function setBinding(id: HotkeyId, binding: string) {
    const action = actionById.get(id)
    if (!action) return
    const next = { ...overrides.value }
    if (binding === action.defaultBinding) delete next[id]
    else next[id] = binding
    overrides.value = next
    persist()
  }

  function reset(id: HotkeyId) {
    const next = { ...overrides.value }
    delete next[id]
    overrides.value = next
    persist()
  }

  function resetAll() {
    overrides.value = {}
    persist()
  }

  const isCustomized = (id: HotkeyId) => overrides.value[id] !== undefined

  return { bindings, bindingOf, matches, conflictOf, setBinding, reset, resetAll, isCustomized }
}
